export const adminThemes = ["Nature", "History", "Food", "Adventure", "Sea", "Culture"];

export const provinceIds = [
  "artvin",
  "rize",
  "trabzon",
  "giresun",
  "ordu",
  "samsun",
  "sinop",
  "kastamonu",
  "bartin",
  "karabuk",
  "zonguldak",
  "duzce",
  "bolu",
  "amasya",
  "tokat",
  "gumushane",
  "bayburt",
  "corum",
];

export const businessCategories = ["hotel", "resort", "pension", "restaurant", "souvenir", "transport", "agency", "guide"];
export const bookingStatuses = ["pending", "confirmed", "cancelled"];
export const subscriptionStatuses = ["active", "cancelled", "expired"];
export const paymentPlanIds = ["basic-monthly", "pro-monthly", "pro-yearly"];
export const userRoles = ["customer", "business", "agency", "guide", "admin"];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asString(value, fallback = "") {
  return typeof value === "string" ? value : fallback;
}

function normalizeDate(value) {
  return asString(value, "");
}

function uniqueStrings(values, fallback = []) {
  const next = asArray(values)
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);

  return next.length ? Array.from(new Set(next)) : fallback;
}

function normalizeHighlight(item, fallbackPrefix) {
  const now = new Date().toISOString();

  return {
    id: asString(item?.id, `${fallbackPrefix}-${Math.random().toString(16).slice(2, 8)}`),
    title: asString(item?.title),
    shortDescription: asString(item?.shortDescription),
    theme: asString(item?.theme, "Nature"),
    locationHint: asString(item?.locationHint),
    bestSeason: asString(item?.bestSeason),
    createdAt: normalizeDate(item?.createdAt || now),
  };
}

export function normalizeProvince(input = {}) {
  return {
    id: asString(input.id),
    name: asString(input.name),
    slogan: asString(input.slogan),
    story: asString(input.story),
    heroImage: input.heroImage || null,
    themes: uniqueStrings(input.themes),
    highlights: asArray(input.highlights).map((item, index) => normalizeHighlight(item, `hl-${input.id || index}`)),
    experiences: asArray(input.experiences).map((item, index) => normalizeHighlight(item, `exp-${input.id || index}`)),
    seasons: uniqueStrings(input.seasons, ["Spring", "Summer", "Autumn", "Winter"]),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeDeal(input = {}) {
  return {
    id: asString(input.id),
    provinceId: asString(input.provinceId),
    title: asString(input.title),
    description: asString(input.description),
    priceText: asString(input.priceText),
    startDate: asString(input.startDate),
    endDate: asString(input.endDate),
    theme: asString(input.theme, "Nature"),
    contactCta: asString(input.contactCta),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

function normalizeBusinessCategory(category) {
  switch (category) {
    case "tour_agency":
      return "agency";
    case "cafe":
      return "restaurant";
    case "museum":
      return "guide";
    default:
      return businessCategories.includes(category) ? category : "hotel";
  }
}

export function normalizeBusiness(input = {}) {
  const location = input.location || {};
  const contact = input.contact || {};
  const provinceId = asString(location.provinceId || input.provinceId);

  return {
    id: asString(input.id),
    name: asString(input.name),
    category: normalizeBusinessCategory(asString(input.category)),
    location: {
      provinceId,
      district: asString(location.district),
      town: asString(location.town),
      addressLine: asString(location.addressLine || input.address),
    },
    contact: {
      phone: asString(contact.phone || input.phone),
      email: asString(contact.email || input.email),
      website: asString(contact.website || input.website),
      whatsapp: asString(contact.whatsapp || input.whatsapp),
    },
    description: asString(input.description),
    priceFromText: asString(input.priceFromText || input.priceText || input.priceRange),
    imageUrl: input.imageUrl || input.image || null,
    ownerUserId: asString(input.ownerUserId),
    updatedAt: normalizeDate(input.updatedAt),
    createdAt: normalizeDate(input.createdAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeBooking(input = {}) {
  const customerName = asString(input.customerName || input.customerDisplayName || input.guestName);

  return {
    id: asString(input.id),
    businessId: asString(input.businessId),
    customerUserId: asString(input.customerUserId),
    customerDisplayName: asString(input.customerDisplayName),
    customerName,
    startDate: asString(input.startDate || input.date),
    nights: Number(input.nights || 1),
    notes: asString(input.notes),
    createdAt: normalizeDate(input.createdAt),
    status: bookingStatuses.includes(input.status) ? input.status : "pending",
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeSubscription(input = {}) {
  return {
    id: asString(input.id || input.userId),
    userId: asString(input.userId),
    planId: paymentPlanIds.includes(input.planId) ? input.planId : "basic-monthly",
    status: subscriptionStatuses.includes(input.status) ? input.status : "active",
    startedAt: asString(input.startedAt || input.createdAt),
    expiresAt: asString(input.expiresAt),
    createdAt: normalizeDate(input.createdAt),
    updatedAt: normalizeDate(input.updatedAt),
    deletedAt: input.deletedAt || null,
  };
}

export function normalizeUserProfile(input = {}) {
  const uid = asString(input.uid || input.userId || input.id);

  return {
    id: uid,
    uid,
    email: input.email ?? null,
    displayName: asString(input.displayName, "Kullanıcı"),
    createdAt: normalizeDate(input.createdAt),
    lastLoginAt: normalizeDate(input.lastLoginAt || input.createdAt),
    role: userRoles.includes(input.role) ? input.role : "customer",
    deletedAt: input.deletedAt || null,
  };
}